import { Box, Modal, Text } from '@fozito/common-ui';
import { Form, InputRef } from 'antd';
import CashierInputNumber from 'components/InputNumber';
import { delay, first } from 'lodash';
import cashierApis from 'modules/POS/services/apis';
import { IProductItem } from 'modules/shop/types/product';
import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { IResponseData } from 'services/response';
import styled from 'styled-components';
import { formatCurrency } from 'utils/formatCurrency';
import { setPageLoading } from 'widgets/PageLoading';
import { cashierCtrl, cashierScannerCtrl } from '../../cashier.controller';
import { IUISelectItem, IUIWeightChoose } from '../../cashier.interface';
import DollarInputPayment from '../../components/DollarAmount/DollarInputPayment';
import { useDollarAmountInputRef } from '../../components/DollarAmount/Input';
import DollarAmountKeyboard from '../../components/DollarAmount/Keyboard';
import ModalActions from '../../components/ModalActions';
import NumberPadV2 from '../../components/NumberPad';
import ProductNameInput, { useProductNameInputRef } from './ProductNameInput';

const FormStyled = styled(Form)``;
type Props = {
  add: (item: IUISelectItem) => void;
};
type Ref = {
  open: () => void;
};
export const useModalWeightRef = () => useRef<Ref>(null);
export const globalModalWeightRef = React.createRef<Ref>();
const ModalWeight = forwardRef<Ref, Props>(({ }, ref) => {
  const barCodeRef = useProductNameInputRef();
  const inputPoundRef = useRef<InputRef>(null);
  const inputPriceRef = useDollarAmountInputRef();
  const [form] = Form.useForm();
  const [open, setOpen] = useState(false);
  const [kbFocus, setKBFocus] = useState<string>('pound');
  const onClose = () => {
    cashierScannerCtrl.current?.setForceStop(false);
    setOpen(false);
    form.resetFields();
  };
  const onOpen: Ref['open'] = () => {
    setOpen(true);
    cashierScannerCtrl.current?.setForceStop(true);
    delay(() => barCodeRef.current?.focus(), 500);
  };
  const onKBFocus = (type: string) => () => setKBFocus(type);

  const handleFinish = (values: any) => {
    const data = values as IUIWeightChoose;
    cashierCtrl.addWeightItem(data);
    onClose();
  };

  const handleKeyDown = async (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Tab') {
      event.preventDefault();
      return inputPriceRef.current?.focus();
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      if (!event.target) return;
      const input = event.target as HTMLInputElement;
      const code: string = input.value;
      if (!code?.trim()) return;
      input.value = '';
      setPageLoading(true);
      try {
        const res: IResponseData<IProductItem[]> = await cashierApis.getProductByCode(code);
        const product = first(res.data.data);
        if (product?.productCode === code) {
          form.resetFields();
          form.setFieldValue('product', product);
          form.setFieldValue('pricePerPound', product.sellPrice || 0);
          form.setFieldValue('productName', product.productName);
        }
      } catch (error) {
      } finally {
        setPageLoading(false);
      }

      return;
    }
  };

  const handleCurrencyKeyDown = async (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Tab') {
      event.preventDefault();
      return inputPoundRef.current?.focus();
    }
  };
  const handlePoundKeyDown = async (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Tab') {
      event.preventDefault();
      return barCodeRef.current?.focus();
    }
  };

  useImperativeHandle(ref, () => ({
    open: onOpen
  }));
  if (!open) return null;
  return (
    <Modal title='weight' open={open} onClose={onClose} width={900} styles={{ body: { maxHeight: 'unset' } }}>
      <FormStyled layout='vertical' form={form} onFinish={handleFinish}>
        <Form.Item noStyle name={'product'} />
        <TopInputs>
          <InputItem>
            <Text style={{ alignSelf: 'stretch' }} color_text_3 text_center content_content_2_16_medium>Product Name/ Bar Code</Text>
            <Form.Item noStyle name={'productName'}>
              <ProductNameInput inputPriceRef={inputPriceRef} ref={barCodeRef} onKeyDown={handleKeyDown} />
            </Form.Item>
          </InputItem>
          <InputItem>
            <Text style={{ alignSelf: 'stretch' }} color_text_3 text_center content_content_2_16_medium>Price/1 pound</Text>
            <Form.Item noStyle name={'pricePerPound'}>
              <InputCurrency
                tabIndex={1}
                withForm
                ref={inputPriceRef as any}
                onFocus={onKBFocus('pricePerPound')}
                onKeyDown={handleCurrencyKeyDown}
              />
            </Form.Item>
          </InputItem>
          <InputItem>
            <Text style={{ alignSelf: 'stretch' }} color_text_3 text_center content_content_2_16_medium>Weight (Pound)</Text>
            <Form.Item noStyle name={'pound'}>
              <CashierInputNumber
                tabIndex={2}
                inputRef={inputPoundRef}
                onFocus={onKBFocus('pound')}
                onKeyDown={handlePoundKeyDown}
              />
            </Form.Item>
          </InputItem>
        </TopInputs>
        <TotalBox>
          <Box display='flex' alignItems='center' justifyContent='center' gap={24} alignSelf='stretch'>
            <Text title_h4_24_medium>TOTAL:</Text>
            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue }) => {
                const pricePerPound = getFieldValue('pricePerPound') || 0;
                const pound = getFieldValue('pound') || 0;
                return <Text title_h1_40_medium>{formatCurrency(pricePerPound * pound)}</Text>;
              }}
            </Form.Item>
          </Box>
        </TotalBox>
        <Form.Item hidden={kbFocus === 'pricePerPound'} style={{ marginBottom: 0 }} name={'pound'}>
          <NumberPadV2 type='CURRENCY' controlRef={inputPoundRef} />
        </Form.Item>
        <Form.Item hidden={kbFocus === 'pound'} style={{ marginBottom: 0 }} name={'pricePerPound'}>
          <DollarAmountKeyboard controlRef={inputPriceRef} />
        </Form.Item>
        <ModalActions onClose={onClose} />
      </FormStyled>
    </Modal>
  );
});
ModalWeight.displayName = 'ModalWeight';
export default ModalWeight;
const InputCurrency = styled(DollarInputPayment)`
&.dollar-input-selector {
  margin-top: 8px;
  align-self: stretch;
  justify-content: center;
  border: none !important;
  .dollar-input-text {
    color: #1D2129;
    font-family: Poppins;
    font-size: 40px;
    line-height: 40px;
    &.text-placeholder {
    line-height: 40px !important;
    }
  }
}
`;
const TotalBox = styled.div`
display: flex;
padding: 0px 40px;
justify-content: center;
align-items: center;
align-self: stretch;
background: #E8FBFF;
`;

const TopInputs = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
`;

const InputItem = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px 40px;
  justify-content: flex-end;
  align-items: center;
  align-self: stretch;
  border-bottom: 1px solid #86909C;
  background: #fff;
  position: relative;
  input:not(.dollar-input-text) {
    border: none;
    align-self: stretch;
    color: #1D2129;
    text-align: center;
    font-family: Poppins;
    font-size: 40px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    outline: none;
    &:active, &:focus {
      border: none;
    }
  }
`;